import { defineStore } from 'pinia';
import { ref } from 'vue';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface Toast {
  id: string;
  type: ToastType;
  title?: string;
  message: string;
  duration?: number;
  undoId?: string; // 撤销操作的ID
  onUndo?: () => void; // 撤销回调
}

export const useToastStore = defineStore('toast', () => {
  const toasts = ref<Toast[]>([]);

  let idCounter = 0;

  function generateId(): string {
    return `toast-${Date.now()}-${++idCounter}`;
  }

  function addToast(toast: Omit<Toast, 'id'>): string {
    const id = generateId();
    const newToast: Toast = {
      id,
      duration: 3000,
      ...toast,
    };

    toasts.value.push(newToast);
    return id;
  }

  function removeToast(id: string) {
    const index = toasts.value.findIndex(toast => toast.id === id);
    if (index !== -1) {
      toasts.value.splice(index, 1);
    }
  }

  function handleUndo(id: string) {
    const toast = toasts.value.find(t => t.id === id);
    if (toast?.onUndo) {
      toast.onUndo();
    }
    removeToast(id);
  }

  function success(message: string, title?: string) {
    return addToast({
      type: 'success',
      title,
      message,
    });
  }

  function error(message: string, title?: string) {
    return addToast({
      type: 'error',
      title: title || '错误',
      message,
      duration: 5000,
    });
  }

  function warning(message: string, title?: string) {
    return addToast({
      type: 'warning',
      title,
      message,
    });
  }

  function info(message: string, title?: string) {
    return addToast({
      type: 'info',
      title,
      message,
    });
  }

  // 带撤销功能的成功提示
  function successWithUndo(message: string, onUndo: () => void, title?: string) {
    return addToast({
      type: 'success',
      title,
      message,
      duration: 4000, // 4秒，给用户足够时间撤销
      onUndo,
    });
  }

  function clear() {
    toasts.value = [];
  }

  return {
    toasts,
    addToast,
    removeToast,
    handleUndo,
    success,
    error,
    warning,
    info,
    successWithUndo,
    clear,
  };
});
